/**
 * JNP smooth scroll — wraps the local Lenis library (assets/lenis.min.js).
 *
 * Provides the smooth, gliding scroll feel (inertia / lerp scrolling) seen on
 * sites like icon-amsterdam.com. Works alongside the theme's built-in
 * reveal-on-scroll animations (animations.js) — Lenis dispatches native scroll
 * events so the IntersectionObserver reveal logic keeps working.
 *
 * Tier 3 custom file — does not touch any upstream Dawn/Blocky asset. All
 * integration with existing UI is done here (no edits to cart-drawer.js etc.).
 *
 * Disabled automatically when:
 *  - the visitor prefers reduced motion, or
 *  - running inside the Shopify theme editor (design mode), where smooth
 *    scroll fights the editor's own scroll-to-section behaviour.
 *
 * Two interop concerns are handled centrally:
 *  - prevent(): nested scrollable containers (cart drawer, menu/mega-menu,
 *    predictive search, facets, quick-add, etc.) keep their own native wheel
 *    scroll instead of Lenis scrolling the page behind them. Detected
 *    generically by walking ancestors for a real vertical overflow, so it
 *    also covers AJAX-injected containers.
 *  - stop()/start(): when an overlay locks the body (Dawn toggles an
 *    `overflow-hidden*` class on <body>), Lenis is paused so the background
 *    cannot be wheel-scrolled behind the overlay; resumed when unlocked.
 */
(function () {
  'use strict';

  // True when `node` (or an ancestor) is itself a vertically scrollable area,
  // so Lenis should leave it to native scrolling.
  function isInsideScrollable(node) {
    var el = node;
    while (el && el !== document.body && el !== document.documentElement) {
      if (el.nodeType === 1) {
        if (el.hasAttribute('data-lenis-prevent')) return true;
        var oy = window.getComputedStyle(el).overflowY;
        if ((oy === 'auto' || oy === 'scroll') && el.scrollHeight > el.clientHeight + 1) {
          return true;
        }
      }
      el = el.parentElement;
    }
    return false;
  }

  function startLenis() {
    if (typeof Lenis === 'undefined') return;

    var prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var inThemeEditor = window.Shopify && window.Shopify.designMode;
    if (prefersReducedMotion || inThemeEditor) return;

    var lenis = new Lenis({
      duration: 1.1, // lower = snappier, higher = more "floaty" (try 0.8–1.4)
      easing: function (t) {
        // easeOutExpo — long, smooth deceleration
        return t === 1 ? 1 : 1 - Math.pow(2, -10 * t);
      },
      smoothWheel: true,
      touchMultiplier: 1.5,
      prevent: isInsideScrollable,
    });

    // Expose for debugging / other scripts.
    window.__jnpLenis = lenis;

    function raf(time) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }
    requestAnimationFrame(raf);

    // Pause Lenis whenever an overlay locks body scroll (Dawn adds an
    // `overflow-hidden` / `overflow-hidden-mobile|tablet|desktop` class to
    // <body>), so the page can't be wheel-scrolled behind it.
    var LOCK_PATTERN = /(^|\s)overflow-hidden(-mobile|-tablet|-desktop)?($|\s)/;
    var isLocked = false;
    function syncLock() {
      var locked = LOCK_PATTERN.test(document.body.className);
      if (locked === isLocked) return;
      isLocked = locked;
      if (locked) {
        lenis.stop();
      } else {
        lenis.start();
      }
    }
    syncLock();
    new MutationObserver(syncLock).observe(document.body, {
      attributes: true,
      attributeFilter: ['class'],
    });
  }

  if (document.readyState === 'loading') {
    window.addEventListener('DOMContentLoaded', startLenis);
  } else {
    startLenis();
  }
})();
